/**
 * Cache de sessão do ritmo de leitura (páginas por minuto).
 *
 * Por que existe: o ppm sai de `getCompletedFocusSessions(uid, 250)` — até 250
 * reads de Firestore por chamada. A estimativa de conclusão vive em DUAS
 * superfícies (o `FocusSheet` e a aba Progresso da BookDetail), e a aba
 * Progresso remonta a cada troca de aba/livro. Sem cache, abrir a aba três
 * vezes custava 750 reads pra devolver o mesmo número.
 *
 * A promessa é compartilhada — duas superfícies montando no mesmo tick
 * disputam UMA query, não duas.
 *
 * Invalidação: explícita quando uma sessão de foco é concluída (o ppm acabou
 * de mudar e o usuário espera ver a estimativa aparecer), com um TTL curto de
 * rede de segurança pro resto.
 */

/** Janela em que o ppm é considerado fresco o bastante pra reaproveitar. */
export const READING_PPM_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  at: number;
  promise: Promise<number | null>;
}

const cache = new Map<string, CacheEntry>();

/**
 * Devolve o ppm do usuário, reaproveitando a query enquanto estiver fresca.
 * `load` só é chamado em cache miss.
 */
export function getCachedReadingPpm(
  uid: string,
  load: () => Promise<number | null>,
): Promise<number | null> {
  const hit = cache.get(uid);
  if (hit && Date.now() - hit.at < READING_PPM_TTL_MS) return hit.promise;

  const entry: CacheEntry = { at: Date.now(), promise: load() };
  cache.set(uid, entry);

  // Falha NÃO fica grudada no cache por 5 min — senão uma query que caiu por
  // rede instável condenaria a estimativa a sumir pela sessão inteira. O
  // `catch` aqui só limpa a entrada; quem consome trata o erro por conta.
  void entry.promise.catch(() => {
    if (cache.get(uid) === entry) cache.delete(uid);
  });

  return entry.promise;
}

/** Descarta o ppm cacheado. Sem `uid`, limpa tudo (ex.: logout). */
export function invalidateReadingPpm(uid?: string): void {
  if (uid) cache.delete(uid);
  else cache.clear();
}
